// chartStacked.ts
// Stacked-area (100%) renderer for ConvergenceChart's "stacked" mode.
// Kept out of ConvergenceChart.tsx so the new mode's logic stays isolated
// from the line/candle renderer. Only valid for mutually-exclusive multi-option
// events, where the shown series' YES probabilities sum to <= ~1.

import * as d3 from 'd3';
import { alpha } from '@mui/material/styles';
import type { OHLCVPoint } from '../../api/types';

interface StackedSeries {
    conditionId: string;
    data: OHLCVPoint[];
}

interface RenderStackedArgs {
    group: d3.Selection<SVGGElement, unknown, null, undefined>;
    series: StackedSeries[];
    xScale: d3.ScaleTime<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    colorMap: ReadonlyMap<string, string>;
    othersColor: string;
    fallbackColor: string;
}

const OTHERS_KEY = '__others__';
// Bands thinner than this (as a fraction of the 0..1 axis) don't get an edge label.
const MIN_LABEL_BAND = 0.03;

// Last known close at-or-before `t`; 0 before the series' first point (LOCF).
function locfClose(data: OHLCVPoint[], t: number): number {
    if (data.length === 0 || t < data[0].t.getTime()) return 0;
    let lo = 0;
    let hi = data.length - 1;
    let ans = 0;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (data[mid].t.getTime() <= t) {
            ans = data[mid].close;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return ans;
}

export function renderStackedAreas({
    group,
    series,
    xScale,
    yScale,
    colorMap,
    othersColor,
    fallbackColor,
}: RenderStackedArgs): void {
    const withData = series.filter(s => s.data.length > 0);
    if (withData.length === 0) return;

    // Unified, sorted, de-duplicated time grid across every series so d3.stack
    // sees one aligned row per timestamp.
    const gridSet = new Set<number>();
    for (const s of withData)
        for (const p of s.data) gridSet.add(p.t.getTime());
    const grid = Array.from(gridSet).sort((a, b) => a - b);
    if (grid.length === 0) return;

    interface StackRow {
        t: Date;
        values: Record<string, number>;
    }
    const rows: StackRow[] = grid.map(ms => {
        const values: Record<string, number> = {};
        let sum = 0;
        for (const s of withData) {
            const v = Math.max(0, locfClose(s.data, ms));
            values[s.conditionId] = v;
            sum += v;
        }
        // Everything outside the shown top-N, so the stack reaches 100%.
        values[OTHERS_KEY] = Math.max(0, 1 - sum);
        return { t: new Date(ms), values };
    });

    const keys = [...withData.map(s => s.conditionId), OTHERS_KEY];
    const stacked = d3
        .stack<StackRow>()
        .keys(keys)
        .value((d, key) => d.values[key] ?? 0)
        .order(d3.stackOrderNone)
        .offset(d3.stackOffsetNone)(rows);

    const areaGen = d3
        .area<d3.SeriesPoint<StackRow>>()
        .x(d => xScale(d.data.t))
        .y0(d => yScale(d[0]))
        .y1(d => yScale(d[1]))
        .curve(d3.curveMonotoneX);

    stacked.forEach((layer, i) => {
        const key = keys[i];
        const isOthers = key === OTHERS_KEY;
        const color = isOthers
            ? othersColor
            : colorMap.get(key) ?? fallbackColor;

        group
            .append('path')
            .datum(layer)
            .attr('d', areaGen)
            .attr('fill', alpha(color, isOthers ? 0.14 : 0.72))
            .attr('stroke', alpha(color, isOthers ? 0.3 : 0.9))
            .attr('stroke-width', 0.75);

        // Right-edge label with the band's current share.
        const last = layer[layer.length - 1];
        if (!last) return;
        const bandVal = last[1] - last[0];
        if (bandVal < MIN_LABEL_BAND) return;
        const midY = (yScale(last[0]) + yScale(last[1])) / 2;
        group
            .append('text')
            .attr('x', xScale(last.data.t) - 4)
            .attr('y', midY + 3)
            .attr('fill', isOthers ? othersColor : color)
            .attr('font-size', '10px')
            .attr('font-family', 'monospace')
            .attr('font-weight', 700)
            .attr('text-anchor', 'end')
            .attr('opacity', isOthers ? 0.6 : 0.95)
            .style('pointer-events', 'none')
            .text(`${(bandVal * 100).toFixed(0)}%`);
    });
}
